/**
 * The chat client.
 *
 * Holds state, talks to the backend through the extracted modules, and tells
 * the host when anything changes. Renders nothing.
 *
 * This file is built up in slices. `open()` and the state wiring are here;
 * sending, receiving and voice follow. Methods not yet implemented reject
 * with a clear message rather than silently doing nothing, so a host
 * integrating early finds out immediately.
 */

import type { CaptchaService, ChatClient, ChatClientOptions, ChatError, ChatMessage, ChatState, ChatType, ContactFormInput, SendMessageInput, AttachmentUpload, WidgetConfig, ChatMessageKey } from './types';
import { createStore } from './internal/state/store';
import { label } from './internal/host/i18n';
import { contactFormRequestBody, contactFormSummaryLines } from './internal/forms/contact-payload';
import { normalisePhoneFields } from './internal/forms/phone-fields';
import { describeError, logError, warn } from './internal/log';
import {
  postMessage,
  getPreviousChatMessages,
  TooManyRequestsError,
} from './internal/transport/service';
import { SocketMessagesType } from './internal/transport/wire';
import { sanitizeButtons } from './internal/transport/buttons';
import {
  submitChatWidgetForm,
  FormSubmitError,
  CAPTCHA_CHALLENGE_STATUSES,
  REJECTED_BY_SPAM_LAYER_STATUS,
} from './internal/transport/forms';
import { postFeedback, postInteractiveEvent } from './internal/transport/service';
import { removeLiveChatAuthInfo } from './internal/security/session';
import type { SocketMessage, LiveChatServerMessages, LiveChatMessageResponse } from './internal/transport/wire';
import { manageLiveChatAuthInfo, getLiveChatAuthInfo } from './internal/security/session';
import { CaptchaServiceRegistry } from './internal/security/captcha-registry';
import { capabilitiesFor, isSelectable } from './internal/state/capabilities';
import { validateAndPrepareFiles, FileUploadError } from './internal/upload/uploader';
import { FILE_UPLOAD_CONSTANTS } from './internal/upload/upload-constants';
import {
  requestUploadUrls,
  uploadToStorage,
  notifyBackend,
  logFileUploadError,
  uploadErrorCode,
} from './internal/upload/storage';
import { fetchWidgetConfig, normaliseConfig, ConfigUnavailableError } from './internal/transport/config';
import { fetchDetectedCountry } from './internal/transport/geo';
import {
  clearRequestTokenSession,
  isRequestTokenSpent,
  markRequestTokenConsumed,
  refreshRequestTokenFromOpenApi,
  resolveRequestToken,
  REQUEST_TOKEN_SUBMIT_DELAY_MS,
} from './internal/security/request-token';

import {
  initialState,
  normaliseContactFormInput,
  businessHoursFor,
  settingText,
  applyHostCopy,
  contactFormFields,
  attachmentLimitsFor,
  deriveContactForm,
  notImplemented,
} from './internal/state/config-mapping';

export function createChatClient(options: ChatClientOptions): ChatClient {
  const store = createStore<ChatState>(initialState(options.chatType), deriveContactForm);

  /**
   * The configured type is not always the effective one: `both` and
   * `allInOneChat` let the visitor choose, and the widget tracks that choice
   * separately from the prop. Capabilities always read from this.
   */
  let activeChatType: ChatType = options.chatType;

  let opened = false;
  let openInFlight: Promise<void> | null = null;
  /**
   * Set by close(), never cleared: a closed client is finished. Guards the
   * window listener and the tail of doOpen(), both of which can run after the
   * host has torn the client down — a React effect cleanup calling close()
   * 400ms into an open() used to still land a live socket and go on mutating
   * state for an unmounted panel.
   */
  let closed = false;

  /** A contact-form submit is in flight; a second would burn the same token. */
  let submitInFlight = false;

  /**
   * The state a closed client rests in. `doOpen()` sets `canSend` and loads
   * history before it reaches the socket, so a close() landing mid-open left a
   * client that reported `connection: 'idle'` and `canSend: true` — a host
   * would enable its composer on a client that will never connect. Both close()
   * and the guards inside doOpen() settle here.
   */
  const settleClosed = () => {
    store.setState({ connection: 'idle', canSend: false, sendBlockedUntil: null });
  };


  const fail = (error: ChatError) => {
    store.setState({ error, requestState: 'error' });
  };

  /** Drop anything tied to a specific conversation. Called when the visitor
   *  switches chat type or goes back to the selection screen. */
  const teardownConversation = () => {
    conversationStarted = false;
    outsideHoursMessageShown = false;
    disconnectConversation();
    abortAllUploads();
    historyCursor = null;
    store.setState({
      connection: 'idle',
      messages: [],
      hasMoreHistory: false,
      historyLoading: false,
      attachments: [],
      agentTyping: false,
      unreadCount: 0,
      contactDetailsRequested: false,
      contactFormSubmitted: false,
      feedbackRequested: false,
      feedbackSubmitted: false,
      ended: false,
      endedReason: null,
    });
  };

  /** The transport reads six fields off this; ServerConfig satisfies it too. */
  const transportConfig = () => ({
    locationId: options.locationId,
    marketplaceURL: options.marketplaceUrl,
    widgetId: options.widgetId,
    defaultWidget: false,
    enableContactForm: !!store.getState().config?.contactForm?.enabled,
  });

  /**
   * Interactive-card analytics. The widget posts these four events from its
   * renderer; a generated host has no renderer of ours, so the core fires the
   * three it can observe (delivered, timed out, clicked) and exposes
   * trackLinkClick() for the one only the host sees.
   * Without this, card dashboards counted zero for every core-hosted site.
   */
  const trackCard = (event: 'card_delivered' | 'card_clicked' | 'card_timed_out' | 'link_clicked', source?: string) => {
    postInteractiveEvent({ serverConfig: transportConfig(), event, source });
  };

  /** The card an interactive event belongs to, for its `source` tag. */
  const cardSource = (messageId?: string) =>
    messageId ? store.getState().messages.find(m => m.id === messageId)?.interactive?.source : undefined;

  let pendingSeq = 0;

  /** Cancel handles for PUTs still in flight, by attachment id. */
  const activeUploads = new Map<string, () => void>();
  const abortAllUploads = () => {
    activeUploads.forEach(abort => abort());
    activeUploads.clear();
  };

  /** Set when /open did not give us a token; see where it is read. */
  let tokenFetchFailed = false;

  /**
   * In-flight /open, so two callers cannot mint two tokens.
   *
   * open() asks for one without awaiting, and a submit asks again if storage
   * looks empty. Before this, a visitor who submitted quickly started a
   * second /open while the first was still running: two tokens minted, the
   * slower response overwriting the faster one in storage, and the submit
   * carrying whichever landed. If the server honours only its most recent
   * token, that is "requestToken verification failed" — with nothing wrong
   * with the token we sent except that we had asked for another one.
   */
  let tokenInFlight: Promise<string> | null = null;

  // ---------------------------------------------------------------------
  // Captcha
  //
  // The challenge is a real widget that needs somewhere in the host's DOM to
  // live, which is the one thing a headless core cannot decide. The host says
  // where; the core owns everything else about it.
  // ---------------------------------------------------------------------

  let captchaAnchor: Element | string | null = options.captchaAnchor ?? null;

  /** Resolved on use, not on set: a host that re-renders between the two
   *  would otherwise leave us holding a node no longer in the document. */
  const anchorElement = (): HTMLElement | null => {
    if (!captchaAnchor) return null;
    const found =
      typeof captchaAnchor === 'string' ? document.querySelector(captchaAnchor) : captchaAnchor;
    return (found as HTMLElement) || null;
  };

  // The widget picks its keys by environment and compiles them in, so they
  // stay inside a built bundle rather than being emitted into host source.
  const staging =
    options.marketplaceUrl.includes('staging') || options.marketplaceUrl.includes('localhost');

  /**
   * A separate chunk, fetched when the chat opens rather than with the page.
   *
   * The wrapper is ~8 kB that a visitor who never opens the chat should not
   * download. Once they do, the widget's behaviour is the right one: fetch
   * the wrapper and Cloudflare's script straight away (see preloadCaptcha),
   * so a challenge later renders at once rather than after a download.
   */
  const captcha = async (): Promise<CaptchaService | null> => {
    if (options.enableCaptcha === false) return null;
    if (options.captcha) return options.captcha;

    const host = anchorElement();
    if (!host) return null;

    const turnstile = await import('./internal/security/turnstile-captcha');

    return turnstile.TurnstileCaptchaService.initCaptchaService({
      host,
      enableCaptcha: true,
      siteKey:
        options.captchaSiteKey ||
        (staging ? turnstile.TURNSTILE_BETA_TOKEN : turnstile.TURNSTILE_ALPHA_TOKEN),
      managedSiteKey: staging
        ? turnstile.TURNSTILE_MANAGED_BETA_TOKEN
        : turnstile.TURNSTILE_MANAGED_ALPHA_TOKEN,
    }) as unknown as CaptchaService;
  };

  /**
   * Put a challenge in front of the visitor.
   *
   * `interactive` is the difference between the two site keys: a 490 means
   * the server wants them to actually do something, not just be scored.
   */
  const presentCaptcha = (interactive: boolean, statusMessage = '') => {
    // Flagged immediately so a host can reveal its anchor; the challenge
    // itself follows once the chunk has loaded.
    store.setState({ captchaRequired: true, captchaStatusMessage: statusMessage });
    listenForCaptchaOutcome();

    void captcha()
      .then(service => {
        if (!service) {
          // Every reason a challenge cannot be drawn, named. Silence here is
          // what made this look like "the captcha does not work".
          warn(
            'a challenge was requested but cannot be drawn: ' +
              (options.enableCaptcha === false
                ? 'enableCaptcha is false'
                : !captchaAnchor
                  ? 'no captchaAnchor was set'
                  : `captchaAnchor ${JSON.stringify(captchaAnchor)} matched no element`),
          );
          return;
        }
        CaptchaServiceRegistry.set(service);
        // The service starts out "verified", which means no challenge is
        // outstanding, and it draws nothing while that holds. The widget
        // flips the flag before every render. Without this line the anchor
        // gets positioned, captchaRequired turns true, and no challenge ever
        // appears — which is exactly how this looked.
        service.isCaptchaVerified = false;
        // One call only. If Turnstile's script is not on the page yet the
        // service fetches it and renders when it arrives; asking it to load
        // as well starts a second wait that re-renders on top of the first.
        service.resetOrRenderCaptcha(anchorElement(), interactive);
        armCaptchaWatchdog(interactive);

        // The service builds its challenge in a fixed portal on document.body
        // and shows it, but never gives it a size or a place — in the widget
        // that is recaptcha-container's job. Without this the challenge is
        // visible at 0×0 in the corner, which is to say not visible. The same
        // module locks the anchor — blur, spinner, status line — which is
        // most of what a visitor sees of a check; the Cloudflare box itself
        // is often gone within a second.
        void import('./internal/security/captcha-portal').then(portal => {
          if (options.captchaLock !== false) {
            portal.lockCaptchaAnchor(anchorElement(), { message: statusMessage, interactive });
            anchorLocked = true;
          }
          stopTrackingPortal?.();
          stopTrackingPortal = portal.trackCaptchaPosition(anchorElement);
        });
      })
      .catch(e => {
        logError('the captcha could not be loaded, so a challenge cannot be answered', e);
      });
  };

  const captchaToken = () => CaptchaServiceRegistry.get()?.getResolvedCaptchaToken();

  /**
   * The widget fetches Cloudflare's script the first time its panel opens,
   * so a challenge later appears at once instead of after a download. Nothing
   * is drawn: the service only renders while a challenge is outstanding.
   */
  const preloadCaptcha = () => {
    void captcha()
      .then(service => {
        if (!service) return;
        CaptchaServiceRegistry.set(service);
        service.loadCaptchaScript();
      })
      .catch(e => {
        warn('could not preload the captcha; it will be fetched when first needed', {
          reason: describeError(e),
        });
      });
  };

  /**
   * Stand the challenge down: nothing outstanding, portal hidden, token gone.
   *
   * Used in three places, all copied from the widget: after the server
   * accepts a submission that carried a token (tokens are single-use, and
   * sending a spent one again earns a fresh 429); when the challenge fails
   * past its retries; and when the watchdog gives up on it.
   */
  const dismissCaptcha = () => {
    clearCaptchaWatchdog();
    const service = CaptchaServiceRegistry.get();
    if (service) {
      service.reset();
      service.hideCaptchaUI?.();
      service.isCaptchaVerified = true;
    }
    clearCaptchaPresentation();
    store.setState({ captchaRequired: false, captchaStatusMessage: '' });
  };

  /**
   * A challenge that never resolves — script blocked, Cloudflare unreachable,
   * a scored check that never calls back — would otherwise leave the visitor
   * with a disabled submit and nothing to do about it. The widget gives it
   * 20 seconds and then hands the decision back to the server.
   */
  const CAPTCHA_WATCHDOG_MS = 20_000;
  /**
   * A visible (managed) challenge needs a person to act, so a 20s budget
   * stands it down while they are still solving it. The widget separates the
   * two budgets for the same reason (captchaWatchdog.ts, PR #351).
   */
  const INTERACTIVE_CAPTCHA_WATCHDOG_MS = 120_000;
  let captchaWatchdog: ReturnType<typeof setTimeout> | null = null;

  const clearCaptchaWatchdog = () => {
    if (captchaWatchdog) clearTimeout(captchaWatchdog);
    captchaWatchdog = null;
  };

  const armCaptchaWatchdog = (interactive: boolean) => {
    clearCaptchaWatchdog();
    const budgetMs = interactive ? INTERACTIVE_CAPTCHA_WATCHDOG_MS : CAPTCHA_WATCHDOG_MS;
    captchaWatchdog = setTimeout(() => {
      captchaWatchdog = null;
      if (CaptchaServiceRegistry.get()?.isCaptchaVerified) return;
      if (!store.getState().captchaRequired) return;
      warn(`the challenge did not resolve in ${budgetMs / 1000}s; standing it down so the visitor can try again`);
      dismissCaptcha();
    }, budgetMs);
  };

  /**
   * The challenge reports its outcome on window events, and until these are
   * listened for the core never learns it was passed — so captchaRequired
   * stays true, the host keeps its submit disabled, and the token the
   * challenge produced is never attached to anything.
   *
   * Deliberately not auto-resubmitting. The visitor pressed send once; doing
   * it again on their behalf risks sending twice, and a scored challenge can
   * resolve at any moment. Clearing the flag hands the decision back to them.
   */
  const captchaEvents: Array<[string, EventListener]> = [];
  let stopTrackingPortal: (() => void) | null = null;
  let anchorLocked = false;

  /** Take the blur off the anchor. Loads the module only if it ever locked. */
  const unlockAnchor = () => {
    if (!anchorLocked) return;
    anchorLocked = false;
    void import('./internal/security/captcha-portal').then(portal => portal.unlockCaptchaAnchor());
  };

  /** The check is over, one way or another: nothing left on screen for it. */
  const clearCaptchaPresentation = () => {
    stopTrackingPortal?.();
    stopTrackingPortal = null;
    unlockAnchor();
  };

  const listenForCaptchaOutcome = () => {
    if (captchaEvents.length) return;

    const on = (event: string, handler: EventListener) => {
      window.addEventListener(event, handler);
      captchaEvents.push([event, handler]);
    };

    on('lc-captcha-verified', () => {
      // The service hides the portal itself on verification; the lock, the
      // position tracking and the watchdog are ours to stop.
      clearCaptchaWatchdog();
      clearCaptchaPresentation();
      store.setState({ captchaRequired: false, captchaStatusMessage: '', error: null });
    });

    // Expired: the visitor has to go again, so the prompt stays.
    on('lc-captcha-expired', () => store.setState({ captchaRequired: true }));

    // An error is not yet a failure. The service retries twice on its own
    // and reports 'lc-captcha-failed' only once those are spent; reporting
    // here would show the visitor an error while the retry is being drawn.
    on('lc-captcha-error', () => warn('the challenge errored; the service is retrying it'));

    // Out of retries. The widget stands the challenge down here so the
    // visitor can at least submit again — the server will challenge afresh
    // if it still wants proof.
    on('lc-captcha-failed', () => {
      dismissCaptcha();
      fail({
        code: 'captcha_failed',
        message: label('captcha_incomplete', 'The check could not be completed. Please try again.'),
        retryable: true,
      });
    });
  };

  const stopListeningForCaptcha = () => {
    for (const [event, handler] of captchaEvents) {
      window.removeEventListener(event, handler);
    }
    captchaEvents.length = 0;
    clearCaptchaWatchdog();
    clearCaptchaPresentation();
  };

  const patchMessage = (id: string, patch: Partial<ChatMessage>) => {
    store.update(state => ({
      messages: state.messages.map(m => (m.id === id ? { ...m, ...patch } : m)),
    }));
  };

  /**
   * Ask /open for a token and persist it.
   *
   * The widget does exactly this, once, when the panel is first opened — it
   * calls the endpoint rather than checking storage first. Reusing a stored
   * token here instead would mean /open is never called for a returning
   * visitor, and they submit with whatever was left over from last time.
   */
  const fetchRequestToken = (reason?: 'refresh'): Promise<string> => {
    if (tokenInFlight) return tokenInFlight;

    tokenInFlight = refreshRequestTokenFromOpenApi({
      marketplaceURL: options.marketplaceUrl,
      locationId: options.locationId,
      widgetId: options.widgetId,
      chatType: activeChatType,
      source: options.source,
      reason,
    }).finally(() => {
      tokenInFlight = null;
    });

    return tokenInFlight;
  };

  /**
   * Submit-time token: prefer what open() persisted, and fetch only if there
   * is none. A freshly fetched token needs the minimum submit interval waited
   * out before it is used, which is why this is not the same as open()'s job.
   */
  const resolveTokenForSubmit = async (): Promise<string> => {
    // Wait for open()'s fetch rather than racing it. Asking again would mint
    // a second token and strand the first.
    //
    // Caught, because this is awaited outside submitContactForm's try block:
    // a rejection here would throw straight out of the method, past every
    // error path, and surface as an unhandled rejection with no submit and
    // nothing said about why.
    if (tokenInFlight) {
      try {
        await tokenInFlight;
      } catch {
        // The fetch failed; fall through and decide on what is in storage.
      }
    }

    const existing = resolveRequestToken({
      locationId: options.locationId,
      widgetId: options.widgetId,
    });
    if (existing) return existing;

    const fresh = await fetchRequestToken('refresh').catch(() => '');
    if (fresh) {
      store.setState({ sendBlockedUntil: Date.now() + REQUEST_TOKEN_SUBMIT_DELAY_MS });
      await new Promise(resolve => setTimeout(resolve, REQUEST_TOKEN_SUBMIT_DELAY_MS));
      store.setState({ sendBlockedUntil: null });
    }
    return fresh;
  };

  const authInfo = () =>
    getLiveChatAuthInfo({
      locationId: options.locationId,
      widgetId: options.widgetId,
      defaultWidget: false,
    });

  /** Panel state, so an inbound message can decide whether it is unread. */
  let panelOpen = false;

  const addIncoming = (message: Partial<ChatMessage>) => {
    if (message.id && store.getState().messages.some(existing => existing.id === message.id)) return;

    store.update(s => ({
      messages: [
        ...s.messages,
        {
          id: message.id || `in-${++pendingSeq}`,
          body: message.body || '',
          direction: 'inbound' as const,
          dateAdded: message.dateAdded || new Date().toISOString(),
          status: 'sent' as const,
          attachments: message.attachments,
          buttons: message.buttons && sanitizeButtons(message.buttons),
          imageUrl: message.imageUrl,
          interactive: message.interactive,
        },
      ],
      // Only count it unread if they are not looking at it.
      unreadCount: panelOpen ? s.unreadCount : s.unreadCount + 1,
    }));
  };

  /**
   * End the conversation, with the configured explanation as a system line.
   *
   * canSend closes: a composer that still looks usable when nothing will
   * reach anyone is worse than one visibly shut.
   */
  const endConversation = (
    reason: NonNullable<ChatState['endedReason']>,
    /** '' when the caller has its own line, or none to show. */
    messageKey: ChatMessageKey | '',
  ) => {
    const line = messageKey ? store.getState().config?.messages?.[messageKey] : undefined;
    if (line) {
      store.update(s => ({
        messages: [
          ...s.messages,
          {
            id: `system-${++pendingSeq}`,
            body: line,
            direction: 'system' as const,
            dateAdded: new Date().toISOString(),
            status: 'sent' as const,
          },
        ],
      }));
    }
    store.setState({
      ended: true,
      endedReason: reason,
      canSend: false,
      agentTyping: false,
      connection: 'idle',
    });
    // Remembered, as the widget does, so a reload does not reconnect to a
    // conversation the server considers finished. restartConversation clears
    // it along with the rest of the identity.
    manageLiveChatAuthInfo({
      data: {
        locationId: options.locationId,
        widgetId: options.widgetId,
        socketConnected: false,
        chatEndedDueToInactivity: true,
      },
    });
  };

  const outsideHours = () => {
    const hours = store.getState().config?.businessHours;
    return !!hours && hours.enabled && !hours.withinHours;
  };

  /** Whether the welcome or offline line has been added for this thread. */
  let conversationStarted = false;
  let outsideHoursMessageShown = false;

  const addSystemLine = (body: string) => {
    store.update(s => ({
      messages: [
        ...s.messages,
        {
          id: `system-${++pendingSeq}`,
          body,
          direction: 'system' as const,
          dateAdded: new Date().toISOString(),
          status: 'sent' as const,
        },
      ],
    }));
  };

  /**
   * Outside business hours: say so once, and stop inviting messages. The
   * widget hides its composer and shows the contact form instead, unless the
   * form was already submitted.
   */
  const applyOutsideHours = () => {
    const config = store.getState().config;
    if (!config) return;
    if (!outsideHoursMessageShown) {
      addIncoming({ body: config.businessHours.outsideHoursMessage });
      outsideHoursMessageShown = true;
    }
    store.setState({
      canSend: false,
      contactDetailsRequested: !store.getState().contactFormSubmitted,
    });
  };

  /**
   * Take on the identity the server just handed back.
   *
   * Two things can change it mid-thread. A contact-details submit may merge
   * this visitor into an existing contact: the server answers with that
   * contact's conversation (`masterConversationId`) and a new visitor and
   * token, and the old ones are gone. A plain send can report the merge too.
   * The socket is bound to the visitor, so when the visitor changes the old
   * socket receives nothing and has to be replaced — the messages the visitor
   * sends still work over REST, which is why this failure is silent unless
   * handled.
   */
  const adoptIdentity = (fresh: {
    visitorId?: string;
    token?: string;
    conversationId?: string;
    masterConversationId?: string;
    contactId?: string;
  }) => {
    const before = authInfo();
    const conversationId = fresh.masterConversationId || fresh.conversationId;
    manageLiveChatAuthInfo({
      data: {
        visitorId: fresh.visitorId,
        token: fresh.token,
        conversationId,
        contactId: fresh.contactId,
        locationId: options.locationId,
        widgetId: options.widgetId,
        // connectSocket refuses while this is false, and the store defaults
        // it to false. The pane flips it just before connecting
        // (updateAuthInfoWithSocketStatus(true)); so does this. It also tells
        // the next page load to reconnect rather than offer a fresh chat.
        socketConnected: !outsideHours(),
      },
    });
    const visitorChanged = !!fresh.visitorId && !!before.visitorId && fresh.visitorId !== before.visitorId;
    const tokenChanged = !!fresh.token && !!before.token && fresh.token !== before.token;
    if (conversationId && before.conversationId && conversationId !== before.conversationId) {
      warn('the conversation was merged into an existing one', {
        from: before.conversationId,
        to: conversationId,
      });
    }
    if ((visitorChanged || tokenChanged) && !store.getState().ended) {
      warn('the visitor identity changed; reconnecting the live connection for the new one');
      disconnectConversation();
      void connectConversation();
    }
  };

  /**
   * The thread has begun — the first message went out, or the contact form
   * was accepted. The widget greets the visitor here (pane.handleStartLiveChat)
   * with the configured intro line, or the offline line outside hours.
   */
  const startConversation = () => {
    if (conversationStarted) return;
    conversationStarted = true;
    const config = store.getState().config;
    if (!config) return;
    if (config.businessHours.enabled && !config.businessHours.withinHours) {
      applyOutsideHours();
      return;
    }
    if (config.liveChat.showWelcomeMessage && config.liveChat.welcomeMessage) {
      addIncoming({ body: config.liveChat.welcomeMessage });
    }
  };

  /**
   * Socket events, mapped to state. The widget reacts to each of these by
   * touching the DOM — spinners, forms, sounds. A host cannot be told to do
   * that, so each becomes a fact in the snapshot and the host decides how to
   * show it.
   */
  /**
   * One handler per socket event. A map rather than a switch so an event the
   * core does not model is a missing key, not a silent fall-through, and so
   * each behaviour can be read (and tested) on its own.
   */
  const socketMessageHandlers: Partial<Record<SocketMessagesType, (event: SocketMessage) => void>> = {
    [SocketMessagesType.MESSAGE]: event => {
      if ((event.message || '').trim() || event.attachments?.length) {
        addIncoming({ body: event.message, attachments: event.attachments, id: event.messageId });
      }
    },

    // An interactive card. Without this the event hit the handler map's
    // optional dispatch and vanished — no message, no error. A generated host
    // showed the visitor a dead end in a flow the sub-account configured.
    [SocketMessagesType.BUTTON]: event => {
      addIncoming({
        body: event.message,
        id: event.messageId,
        buttons: event.buttons,
        imageUrl: event.imageUrl,
        interactive: event.interactive,
      });
      trackCard('card_delivered', event.interactive?.source);
    },

    // The card was answered or timed out, here or in another tab. Only the
    // interactive state changes; body and buttons stay, so the host redraws
    // the same card with its controls disabled.
    [SocketMessagesType.MESSAGE_UPDATED]: event => {
      if (!event.messageId) return;
      // A card that expires without a click. Mirrors the widget: a card that
      // was already locked was answered, not timed out, so it is not counted.
      const previous = store.getState().messages.find(m => m.id === event.messageId);
      if (event.interactive?.timedOutAt && !previous?.interactive?.locked) {
        trackCard('card_timed_out', event.interactive.source);
      }
      store.update(s => ({
        messages: s.messages.map(m =>
          m.id === event.messageId ? { ...m, interactive: event.interactive ?? m.interactive } : m,
        ),
      }));
    },

    [SocketMessagesType.TYPING_ON]: () => store.setState({ agentTyping: true }),
    [SocketMessagesType.TYPING_OFF]: () => store.setState({ agentTyping: false }),

    // A rating request closes the chat: thumbs, a "start a new chat" prompt,
    // and the socket goes down.
    [SocketMessagesType.FEEDBACK]: () => {
      endConversation('acknowledged', '');
      store.setState({ feedbackRequested: true, contactDetailsRequested: false });
      disconnectConversation();
    },

    // The agent wants a way to reach the visitor: the configured line goes in
    // the thread, and the host puts the form under it.
    [SocketMessagesType.CONTACT_DETAILS_REQUESTED]: () => {
      const line = store.getState().config?.contactForm.requestMessage;
      if (line && !store.getState().contactDetailsRequested) addIncoming({ body: line });
      store.setState({ contactDetailsRequested: true });
    },

    // The visitor went quiet. The chat just ends.
    [SocketMessagesType.VISITOR_INACTIVITY]: () => endConversation('contact_inactive', 'liveChatVisitorInactiveMsg'),

    // Nobody answered. The chat ends, but details are still asked for —
    // treating this as a plain end loses the lead, which is the whole point
    // of the timeout.
    [SocketMessagesType.USER_INACTIVITY]: () => {
      endConversation('agent_unavailable', 'liveChatUserInactiveMsg');
      store.setState({ contactDetailsRequested: true });
    },

    // The agent closed the chat. Feedback is asked for here, not on the
    // inactivity paths.
    [SocketMessagesType.ACKNOWLEDGEMENT]: () => {
      endConversation('acknowledged', 'liveChatAckMsg');
      store.setState({ feedbackRequested: true });
      disconnectConversation();
    },

    // The business is shut: a fixed line, the offline message once, and a
    // request for details unless the form was already submitted — the
    // visitor's message still reaches the inbox.
    [SocketMessagesType.OUTSIDE_OFFICE_HOURS]: () => {
      addSystemLine('Chat Closed: Outside Office Hours');
      endConversation('outside_office_hours', '');
      applyOutsideHours();
    },
  };

  /**
   * Socket events, mapped to state. The widget reacts to each by touching the
   * DOM — spinners, forms, sounds. A host cannot be told to do that, so each
   * becomes a fact in the snapshot and the host decides how to show it.
   */
  const handleSocketMessage = (event: SocketMessage) => {
    socketMessageHandlers[event.event]?.(event);
  };

  const toChatMessage = (m: LiveChatServerMessages): ChatMessage => ({
    // The search endpoint answers with `id`; older shapes used `_id`. Without
    // one, a reconnect could not tell a missed message from one already shown.
    id: m._id || m.id || `hist-${m.dateAdded}-${(m.body || '').length}`,
    // Activity entries and attachment-only messages arrive without a body.
    body: m.body ?? '',
    // The server says 'inbound'/'outbound' from the business's point of view,
    // which is the opposite of the visitor's.
    direction: m.direction === 'outbound' ? 'inbound' : 'outbound',
    dateAdded: m.dateAdded,
    status: 'sent',
    attachments: m.attachments,
    messageType: m.messageType as unknown as number,
    liveChatType: m.liveChatType as unknown as string,
    // History nests an interactive card under `buttonCard`; a live arrival
    // carries `buttons` / `imageUrl` flat. Flattening here means the host sees
    // one shape either way — without it a card came back after a reload as a
    // bare line of text with its buttons gone.
    ...(m.buttonCard
      ? { body: m.body || m.buttonCard.text || '', buttons: sanitizeButtons(m.buttonCard.buttons), imageUrl: m.buttonCard.imageUrl }
      : {}),
    interactive: m.interactive,
  });

  /**
   * Where the next page of history starts: the `lastMessageId` the server
   * returned with the previous page, or null when there is nothing older.
   * The widget passes the same value back on its "fetch older" button.
   */
  let historyCursor: string | null = null;

  const loadHistory = async ({ merge = false }: { merge?: boolean } = {}) => {
    const { conversationId } = authInfo();
    // Nothing to load until a conversation exists — a first-time visitor has
    // no history, and asking for it would be a wasted request on every load.
    if (!conversationId) return;

    try {
      const response = await getPreviousChatMessages({
        serverConfig: transportConfig(),
        conversationId,
      });
      const raw = response?.messages || [];
      const history = raw.map(toChatMessage).reverse();
      // The server marks the newest message of a conversation it has closed.
      // The pane reads this on load and offers a new chat instead of
      // reconnecting; the same flag is what open() checks just after this.
      if (!merge && raw[0]?.chatToBeClosed) {
        manageLiveChatAuthInfo({
          data: {
            locationId: options.locationId,
            widgetId: options.widgetId,
            socketConnected: false,
            chatEndedDueToInactivity: true,
          },
        });
      }
      if (!merge) {
        // A non-empty lastMessageId means the server holds an older page.
        historyCursor = response?.lastMessageId || null;
      }
      store.update(s => {
        if (!merge) {
          // Anything sent in this session stays after the history it follows.
          return { messages: [...history, ...s.messages], hasMoreHistory: historyCursor !== null };
        }
        // After a reconnect: keep what we have, add only what we missed. A
        // message sent from here already carries its server id once acked.
        const seen = new Set(s.messages.map(m => m.id));
        const missed = history.filter(m => !seen.has(m.id));
        if (!missed.length) return {};
        return {
          messages: [...s.messages, ...missed].sort((a, b) => a.dateAdded.localeCompare(b.dateAdded)),
          unreadCount: panelOpen ? s.unreadCount : s.unreadCount + missed.filter(m => m.direction === 'inbound').length,
        };
      });
    } catch (e) {
      // Not worth blocking the chat over — the visitor can still send and
      // live messages still arrive — but a silently empty transcript looks
      // like lost history rather than a failed request.
      warn('could not load previous messages; the conversation starts empty', {
        reason: describeError(e),
        conversationId,
      });
    }
  };

  /**
   * socket.io is loaded here and nowhere else.
   *
   * It is three fifths of the bundle and only live chat opens a connection —
   * an SMS/email, WhatsApp or voice widget would download it to sit unused.
   * Reaching it through anything but a dynamic import puts it back in the
   * entry for every visitor.
   */
  const connectConversation = async () => {
    store.setState({ connection: 'connecting' });
    try {
      const { connectSocket } = await import('./internal/transport/socket');
      const socket = connectSocket({
        serverConfig: transportConfig(),
        isDummy: false,
        onMessage: handleSocketMessage,
        onStatus: (status, detail) => {
          if (store.getState().ended) return;
          store.setState({ connection: status });
          // Anything the agent sent while we were away arrived nowhere.
          // Fetch the thread again and keep what is new.
          if (status === 'connected' && detail?.reconnected) void loadHistory({ merge: true });
          if (status === 'reconnecting') warn('live connection dropped; reconnecting', { reason: detail?.reason });
          if (status === 'offline') warn('live connection lost; sending still works over REST', { reason: detail?.reason });
        },
      });
      // connectSocket returns null when there is nothing to connect yet (no
      // bearer token before the first send) or when the chat has ended.
      if (!socket) store.setState({ connection: 'idle' });
    } catch (e) {
      // Sending still works over REST; only inbound is affected.
      logError('could not connect the live conversation; inbound messages will not arrive', e);
      store.setState({ connection: 'offline' });
    }
  };

  // A laptop lid closing takes the socket with it; socket.io gives up after
  // its retries. Coming back online is the moment to try again.
  const onWindowOnline = () => {
    // A closed client must not reconnect. close() removes this listener, but a
    // late 'online' can still be dispatched against a handler mid-flight, and a
    // host that drops its reference without calling close() keeps this closure
    // alive through the listener itself.
    if (closed) return settleClosed();
    const { connection, ended, config } = store.getState();
    if (ended || !config || !authInfo().token) return;
    // socket.io is backing off for up to ten seconds between attempts; the
    // browser just said the network is back, so go now with a fresh socket.
    // The pane does the same by remounting itself.
    if (connection === 'offline' || connection === 'reconnecting') {
      disconnectConversation();
      void connectConversation();
    }
  };
  if (typeof window !== 'undefined') window.addEventListener('online', onWindowOnline);

  /** Only meaningful once the socket chunk has loaded; a no-op before that. */
  const disconnectConversation = () => {
    void import('./internal/transport/socket').then(m => m.cleanupSocketConnection());
  };

  let countryLookupInFlight = false;

  /**
   * The visitor's country, from the geo endpoint, every time.
   *
   * Only a lookup that actually produced a country is final. The first
   * version latched on "started", so a request that timed out (3s) or came
   * back empty left `detectedCountry` null for the life of the client and the
   * phone field sat on the locale fallback — a visitor in India offered +1,
   * with no way back short of a reload. Re-asking on the next open() costs one
   * request and is the only way the selector can be trusted to show what the
   * API said.
   */
  const resolveDetectedCountry = (): void => {
    if (countryLookupInFlight || store.getState().detectedCountry) return;
    countryLookupInFlight = true;

    void fetchDetectedCountry({ marketplaceUrl: options.marketplaceUrl })
      .then(country => store.setState({ detectedCountry: country, detectedCountryResolved: true }))
      .catch(() => store.setState({ detectedCountry: null, detectedCountryResolved: true }))
      .finally(() => {
        countryLookupInFlight = false;
      });
  };

  const doOpen = async (): Promise<void> => {
    store.setState({ requestState: 'in_progress', error: null });
    resolveDetectedCountry();

    let rawConfig;
    try {
      rawConfig = await fetchWidgetConfig({
        marketplaceUrl: options.marketplaceUrl,
        widgetId: options.widgetId,
        source: options.source,
      });
    } catch (e) {
      fail({
        code: 'config_unavailable',
        message:
          e instanceof ConfigUnavailableError
            ? label('chat_unavailable', 'This chat is unavailable right now. Please try again shortly.')
            : label('chat_service_unreachable', 'Could not reach the chat service.'),
        retryable: true,
      });
      return;
    }

    // Keys arrive kebab-cased and several values are JSON strings, so the
    // payload cannot be handed to a host as-is.
    const settings = normaliseConfig(rawConfig);

    // The server decides the type; the option is only a hint for what to
    // request. An allInOne widget reconfigured to live-only must not keep
    // offering a choice that no longer exists.
    const configuredType = (settings.chatType as ChatType) || activeChatType;
    activeChatType = configuredType;

    const recordConfig: WidgetConfig = {
      chatType: configuredType,
      locale: settings.locale as string | undefined,
      compliance: {
        consentRequired: !!settings.consentRequired,
        showConsentCheckbox: !!settings.showConsentCheckbox,
        defaultConsentCheck: !!settings.defaultConsentCheck,
        consentText: settings.legalMsg as string | undefined,
        a2p: settings.aTwoPCompliance as Record<string, unknown> | undefined,
      },
      contactForm: {
        enabled: !!settings.enableContactForm,
        fields: contactFormFields(settings, configuredType),
        introMessage: (settings.contactFormIntroMessage as string) || 'Please share contact details',
        requestMessage: (settings.contactFormSystemMessage as string) || 'Please share contact details',
      },
      liveChat: {
        // The widget's prop defaults: on, with this line.
        showWelcomeMessage: settings.showLiveChatWelcomeMsg !== false,
        welcomeMessage:
          (settings.liveChatIntroMsg as string) ||
          'Give us a minute to assign you the best person to help you out.',
        feedbackMessage: (settings.liveChatFeedbackMsg as string) || 'Please rate your experience.',
        feedbackNote: (settings.liveChatFeedbackNote as string) || 'Thank you, for taking your time.',
        attachments: attachmentLimitsFor(configuredType),
      },
      businessHours: businessHoursFor(settings.businessOfficeHours),
      theme: settings.theme as Record<string, unknown> | undefined,
      // The widget's prop defaults, so an end-of-chat line is never silently
      // missing on a widget the sub-account left at defaults.
      // Only the keys the core reads, each falling back to the widget's own
      // prop default. Spreading every setting in here put objects, booleans
      // and arrays behind a `string` type and left hosts to find out at
      // runtime; `raw` below is where an unmodelled setting belongs.
      messages: {
        liveChatVisitorInactiveMsg: settingText(settings.liveChatVisitorInactiveMsg, 'Chat closed due to user inactivity'),
        liveChatUserInactiveMsg: settingText(
          settings.liveChatUserInactiveMsg,
          'Looks like no one is available. Please leave your contact details. We will get back to you shortly',
        ),
        liveChatAckMsg: settingText(settings.liveChatAckMsg, 'Your chat has ended'),
      },
      raw: settings,
    };
    const config = applyHostCopy(recordConfig, options.copy);

    store.setState({
      config,
      requestState: 'completed',
      activeChatType: configuredType,
      // A widget that offers a choice has not picked anything yet. The host
      // renders its selection screen off this rather than re-deriving it.
      awaitingChatTypeSelection: isSelectable(configuredType),
    });

    // Always ask /open, as the widget does on first open. Not awaited: the
    // token is only needed at submit time, and blocking the UI on it would
    // delay the form appearing.
    //
    // Its failure is recorded rather than swallowed. refreshRequestTokenFromOpenApi
    // returns '' for everything — a network failure, a non-200, a response
    // with no token in it — and the submit then falls back to whatever is
    // left in session storage. That stale token is rejected with a 401 that
    // looks like a token bug rather than what it is: /open never answered.
    void fetchRequestToken()
      .then(token => {
        if (!token) tokenFetchFailed = true;
      })
      .catch(() => {
        tokenFetchFailed = true;
      });

    // Nothing further to set up until the visitor picks. Connecting now would
    // open a socket for a thread they may never choose.
    if (isSelectable(activeChatType)) return;

    const capabilities = capabilitiesFor(activeChatType);
    if (!capabilities.conversation) {
      // Email/SMS, WhatsApp, Facebook, Instagram and voice have no running
      // thread. Opening a socket and asking for history would be two wasted
      // requests per page load for the majority of widgets.
      store.setState({ connection: 'idle' });
      return;
    }

    // A conversation type can send as soon as it is open: the first message
    // creates the conversation and returns the bearer token.
    store.setState({ canSend: true });
    await loadHistory();
    // close() can land during any await above. Without this the teardown is
    // overtaken: a socket opens after cleanup has already run, and nothing is
    // left holding a reference to close it.
    if (closed) return settleClosed();

    // Ended in an earlier session — inactivity, or the agent closed it. The
    // widget shows the transcript and a "start a new chat" prompt, and does
    // not reconnect; the server would refuse the socket anyway.
    if (authInfo().chatEndedDueToInactivity) {
      store.setState({ ended: true, endedReason: 'ended_earlier', canSend: false, connection: 'idle' });
      return;
    }

    // A returning visitor with a thread has already been greeted.
    if (authInfo().conversationId) conversationStarted = true;

    // A live chat that opens on its contact form shows the configured intro
    // line above it, in the thread — the widget's SHOW_CONTACTS_FORM message.
    if (config.contactForm.enabled && !authInfo().conversationId && config.contactForm.introMessage) {
      addIncoming({ body: config.contactForm.introMessage });
    }

    if (config.businessHours.enabled && !config.businessHours.withinHours) {
      applyOutsideHours();
    }
    if (closed) return settleClosed();
    await connectConversation();
  };

  /**
   * The body of submitContactForm. Split out so the public method owns the
   * in-flight guard and this stays a plain sequence of submit steps.
   */
  const runContactFormSubmit = async (rawInput: ContactFormInput) => {
    const config = store.getState().config;
    const input = normaliseContactFormInput(rawInput, config?.contactForm.fields ?? []);

    // Consent is a precondition, not a field to send along and let the
    // server sort out. Blocking here is what makes consentRequired mean
    // something — and it keys off consentRequired, not off whether a
    // checkbox happens to be shown. A real staging widget has
    // consent-required: false with no checkbox at all.
    if (config?.compliance.consentRequired && !input.consentGiven) {
      fail({
        code: 'consent_required',
        message: label('consent_required', 'Please accept the terms to continue.'),
        retryable: false,
      });
      return;
    }

    // Set before the phone-library await below, not after it: that await can
    // reach a CDN timeout, and until this lands a host has nothing to disable
    // its Submit button on.
    store.setState({ requestState: 'in_progress', error: null });

    // Numbers are reformatted before anything is sent; an unusable one stops
    // here, before the request. See internal/forms/phone-fields.
    const rejected = await normalisePhoneFields(
      config?.contactForm.fields ?? [],
      input as Record<string, unknown>,
      store.getState().detectedCountry,
      config?.locale,
    );
    if (rejected) {
      fail({
        code: 'invalid_phone',
        field: rejected.field,
        message:
          rejected.reason === 'no_country'
            ? label('invalid_phone_country', 'Please include the country code with your phone number, for example +1.')
            : label('invalid_phone', 'Please enter a valid phone number.'),
        retryable: false,
      });
      return;
    }

    const token = await resolveTokenForSubmit();
    if (!token) {
      fail({
        code: 'token_unavailable',
        message: label('session_failed', 'Could not start a secure session. Please try again shortly.'),
        retryable: true,
      });
      return;
    }

    // A leftover token with no fresh one behind it will be refused, and the
    // 401 would read as our bug rather than an unanswered /open.
    //
    // Warned, not put in state.error: that field is what the host shows the
    // visitor, and this is for whoever is debugging. Writing it there also
    // overwrites whatever the submit actually reported.
    if (tokenFetchFailed) {
      warn(
        '/open returned no token; submitting with a stored one. ' +
          'Load the core-debug bundle and call __diagnostics.diagnoseRequestToken to see which step failed.',
      );
    }

    const usedCaptchaToken = captchaToken();
    try {
      const accepted = (await submitChatWidgetForm({
        marketplaceUrl: options.marketplaceUrl,
        locationId: options.locationId,
        widgetId: options.widgetId,
        // The backend uses this to tell a live-chat contact form apart from
        // a standalone form widget, though both post to the same endpoint.
        chatType: activeChatType,
        source: options.source,
        requestToken: token,
        body: contactFormRequestBody({
          input,
          config,
          chatType: activeChatType,
          locationId: options.locationId,
          widgetId: options.widgetId,
          captchaToken: usedCaptchaToken,
          conversationId: authInfo().conversationId,
        }),
      })) as Partial<LiveChatMessageResponse & { masterConversationId?: string }> | undefined;

      // The token that got this through is spent; the widget burns it here.
      if (usedCaptchaToken) dismissCaptcha();

      // For a live chat the form is the start of the thread: the server
      // hands back the conversation and contact, and the widget stores them
      // so the messages that follow land in the same conversation.
      if (capabilitiesFor(activeChatType).conversation && accepted) {
        if (accepted.masterConversationId || accepted.conversationId || (accepted.visitorId && accepted.token)) {
          adoptIdentity(accepted);
        }
        // The widget echoes the submitted details into the thread as the
        // visitor's first message, so the transcript reads as a conversation.
        const lines = contactFormSummaryLines(config?.contactForm.fields || [], input as Record<string, unknown>);
        if (lines.length) {
          store.update(s => ({
            messages: [
              ...s.messages,
              {
                id: `details-${++pendingSeq}`,
                body: lines.join('\n'),
                direction: 'outbound' as const,
                dateAdded: new Date().toISOString(),
                status: 'sent' as const,
              },
            ],
          }));
        }
        startConversation();
        if (authInfo().token && store.getState().connection === 'idle' && !store.getState().ended) {
          void connectConversation();
        }
      }

      // Answered: the host should stop asking, whether it opened the form
      // itself or the server asked for it mid-chat. contactFormSubmitted is
      // the flag a host routes on — requestState says only that the last
      // request finished, and open() completes it too, which is how a
      // generated UI once showed a live-chat composer for an SMS/Email
      // widget before anything was submitted.
      store.setState({
        requestState: 'completed',
        contactDetailsRequested: false,
        contactFormSubmitted: true,
        captchaRequired: false,
      });
    } catch (e) {
      const status = e instanceof FormSubmitError ? e.status : undefined;

      // A 401, any 5xx, or a transport failure may have consumed the token
      // server-side even though the submit failed. Reusing it is answered with
      // another 401, so the visitor's retry fails for a reason that has nothing
      // to do with what they typed. Matches the widget (text-widget.tsx,
      // chat-form.tsx) since PR #351.
      if (token && isRequestTokenSpent(status)) {
        markRequestTokenConsumed(options.locationId, options.widgetId, token);
      }

      const handler = status === undefined ? undefined : submitFailureHandlers[status];
      (handler ?? submitFailed)();
    }
  };
  /** Repeated submit-failure copy. Resolved per call: a host installs its
   *  translator after import, so a captured value would pin English. */
  const sendDetailsFailed = () => label('send_details_failed', 'We could not send your details. Please try again.');
  const verificationFailed = () => label('verification_failed', 'Verification failed. Please try again.');

  /** Every submit failure ends here unless a status claims it. */
  const submitFailed = () =>
    fail({ code: 'send_failed', message: sendDetailsFailed(), retryable: true });

  /**
   * How a submit failure is answered, by HTTP status. A map rather than an if
   * chain: each status is one entry, and a status with no entry is the
   * generic failure rather than a branch someone forgot to add.
   */
  const submitFailureHandlers: Record<number, () => void> = {
    // 490/492 are not failures to report — the server wants the visitor to
    // pass a challenge and try again. Saying it went wrong would hide the one
    // action that helps. No status line: the Cloudflare box is the
    // instruction, so the widget shows the blur alone.
    ...Object.fromEntries(
      [...CAPTCHA_CHALLENGE_STATUSES].map(code => [
        code,
        () => {
          presentCaptcha(true, '');
          store.setState({ requestState: 'error' });
        },
      ]),
    ),

    // A rate limit gets a challenge rather than a dead end: passing it is how
    // the visitor gets through.
    429: () => {
      presentCaptcha(false, label('completing_check', 'Completing security check…'));
      store.setState({ rateLimited: true });
      fail({ code: 'rate_limited', message: label('rate_limited_submit', 'Please complete the check below and try again.'), retryable: true });
    },

    // Past the one token refresh submitChatWidgetForm allows, so the request
    // token is not what was objected to. Treated as a failed verification.
    401: () => {
      presentCaptcha(false, verificationFailed());
      fail({ code: 'captcha_failed', message: verificationFailed(), retryable: true });
    },

    // The edge's anti-spam layer refused the details themselves. Not a
    // challenge — a captcha would loop. The visitor sees the same line as any
    // failed send: naming the spam signal would tell a sender what to change.
    [REJECTED_BY_SPAM_LAYER_STATUS]: () =>
      fail({ code: 'rejected', message: sendDetailsFailed(), retryable: true }),
  };

  return {
    getState: () => store.getState(),
    subscribe: listener => store.subscribe(listener),

    async open() {
      // close() is terminal. Without this, open() after close() ran the whole
      // config + history fetch and then preloadCaptcha() — loading Turnstile
      // onto a client that can never send — before latching `opened`.
      if (closed) {
        warn('open() ignored: this client was closed. Create a new client to start again.');
        return;
      }
      if (opened) return;
      if (openInFlight) return openInFlight;
      openInFlight = doOpen()
        .then(() => preloadCaptcha())
        .then(() => {
          // Only a run that actually loaded the widget counts as open.
          // doOpen() reports a config failure through fail({ retryable: true })
          // and returns normally rather than throwing, so latching `opened` in
          // a finally() marked a failed open as done: every later open() hit
          // the `if (opened) return` guard and the retry control a host is told
          // to build became a no-op for the life of the page. The module is
          // pinned into generated sites at build time, so that is unfixable
          // from our side once shipped.
          if (store.getState().requestState !== 'error') opened = true;
        })
        .finally(() => {
          openInFlight = null;
        });
      return openInFlight;
    },

    trackLinkClick(messageId?: string) {
      trackCard('link_clicked', cardSource(messageId));
    },

    close() {
      closed = true;
      if (typeof window !== 'undefined') window.removeEventListener('online', onWindowOnline);
      disconnectConversation();
      stopListeningForCaptcha();
      abortAllUploads();
      opened = false;
      openInFlight = null;
      settleClosed();
    },

    setPanelOpen(open: boolean) {
      panelOpen = open;
      if (open) store.setState({ unreadCount: 0 });
    },

    setCaptchaAnchor(target: Element | string | null) {
      captchaAnchor = target;
      // If something is already waiting on a challenge, draw it now that
      // there is somewhere to put it.
      if (target && store.getState().captchaRequired) {
        presentCaptcha(false, store.getState().captchaStatusMessage);
      }
    },

    setActiveChatType(chatType: ChatType | null) {
      const configured = (store.getState().config?.chatType as ChatType) || options.chatType;

      // Back: return to the selection screen. Only a widget that offers a
      // choice has one to return to.
      if (chatType === null) {
        if (!isSelectable(configured)) return;
        teardownConversation();
        activeChatType = configured;
        store.setState({ activeChatType: configured, awaitingChatTypeSelection: true });
        return;
      }

      if (chatType === activeChatType) return;

      // Leaving a live thread: close the connection. Left open, messages
      // arrive for a conversation the visitor has navigated away from.
      teardownConversation();
      activeChatType = chatType;
      store.update(s => ({
        activeChatType: chatType,
        awaitingChatTypeSelection: false,
        // The form differs by type: no message field in a live chat.
        config: s.config
          ? { ...s.config, contactForm: { ...s.config.contactForm, fields: contactFormFields(s.config.raw ?? {}, chatType) } }
          : s.config,
      }));
    },

    async sendMessage(input: SendMessageInput) {
      const text = (input.text || '').trim();
      const state = store.getState();

      if (!capabilitiesFor(activeChatType).conversation) {
        throw new Error('chat core: this chat type has no message thread');
      }
      // The visitor clicked a card button. Counted here rather than after the
      // send so a rejected send still records the click, which is what the
      // widget does.
      if (input.buttonResponse) trackCard('card_clicked', cardSource(input.buttonResponse.messageId));
      if (!text && !input.attachments?.length) {
        if (input.buttonResponse) {
          warn('sendMessage({ buttonResponse }) needs the button title as `text`; nothing was sent.');
        }
        return;
      }

      // The host is told not to send while this is false; enforce it here too
      // rather than trusting every host to have read that.
      if (!state.canSend) {
        fail({
          code: 'send_failed',
          message: label('send_too_soon', 'Just a moment before sending again.'),
          retryable: true,
        });
        return;
      }

      // Show it straight away. A visitor watching their own message hang for
      // the round trip assumes it failed and sends it twice.
      const localId = `pending-${++pendingSeq}`;
      store.update(s => ({
        messages: [
          ...s.messages,
          {
            id: localId,
            body: text,
            direction: 'outbound' as const,
            dateAdded: new Date().toISOString(),
            status: 'pending' as const,
            attachments: input.attachments,
          },
        ],
        error: null,
      }));

      try {
        // Read once, here: a token is single-use, and if the server accepts
        // this message the challenge is stood down below.
        const usedCaptchaToken = captchaToken();
        const response = await postMessage({
          serverConfig: transportConfig(),
          messageText: text,
          attachments: input.attachments,
          captchaToken: usedCaptchaToken,
          subType: activeChatType === 'allInOneChat' ? 'ALL_IN_ONE_CHAT' : undefined,
          // postMessage stamps subType: 'button_response' when this is set,
          // which is how the backend resumes the clicked branch.
          buttonResponse: input.buttonResponse,
        });

        // The first send is what creates the conversation and returns the
        // bearer token every later call needs, so it has to be persisted here
        // rather than at open().
        adoptIdentity(response as typeof response & { masterConversationId?: string });
        startConversation();

        // The conversation exists from here on, so a first-time visitor's
        // socket can only be opened now.
        if (store.getState().connection === 'idle') void connectConversation();

        patchMessage(localId, {
          id: response.messageDetails?.id || localId,
          dateAdded: response.messageDetails?.dateAdded || new Date().toISOString(),
          status: 'sent',
        });
        // The files this message carried are on their way; the ones it did
        // not (still uploading, or failed) stay for the next one.
        const sent = new Set(input.attachments || []);
        store.update(s => ({
          attachments: sent.size ? s.attachments.filter(a => !a.url || !sent.has(a.url)) : s.attachments,
          rateLimited: false,
          rateLimitedUntil: null,
        }));
        if (usedCaptchaToken) dismissCaptcha();
      } catch (e) {
        // Keep the message visible and marked failed. Dropping it loses what
        // the visitor typed.
        patchMessage(localId, { status: 'failed' });
        if (!(e instanceof TooManyRequestsError)) logError('the message was rejected', e);

        if (e instanceof TooManyRequestsError) {
          store.setState({ rateLimited: true });
          fail({
            code: 'rate_limited',
            message: label('too_many_messages', 'Too many messages just now. Please wait a moment.'),
            retryable: true,
          });
          return;
        }
        if (e instanceof Error && e.message === 'ConversationIsDeleted') {
          // Only reached when the widget has a contact form: without one the
          // transport re-sends as a new visitor by itself. With one, the
          // identity has been dropped and the widget asks for details again
          // before the new conversation starts — so does this.
          conversationStarted = false;
          store.setState({ contactDetailsRequested: true, contactFormSubmitted: false });
          fail({
            code: 'conversation_deleted',
            message: label('conversation_ended', 'That conversation had ended. Please share your details to start a new one.'),
            retryable: true,
          });
          return;
        }
        fail({
          code: 'send_failed',
          message: label('message_failed', 'Your message could not be sent. Please try again.'),
          retryable: true,
        });
      }
    },
    async submitContactForm(rawInput: ContactFormInput) {
      // The request token is single-use. Two submits in flight means the
      // second spends a token the first already burned and is answered 401,
      // so a double-click showed a verification failure for a form that was
      // submitting correctly. The guard sits at the entry rather than after
      // the phone-library await below, because that await is the window a
      // second click lands in.
      if (submitInFlight) {
        // Not an error, and nothing the visitor should see — but a host
        // wondering why its second call did nothing needs to be able to find
        // out. Enforced here rather than trusting every host to disable its
        // own button, which is what requestState is for.
        warn('a contact-form submit is already in flight; ignoring this one');
        return;
      }
      submitInFlight = true;
      try {
        await runContactFormSubmit(rawInput);
      } finally {
        submitInFlight = false;
      }
    },
    async uploadAttachment(file: File): Promise<AttachmentUpload> {
      const failedEntry = (): AttachmentUpload => ({
        id: `rejected-${++pendingSeq}`,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'failed',
        progress: 0,
      });

      // Only a thread can carry a file. The widget shows no attach control
      // on an SMS/Email form, and the form endpoint takes none.
      if (!capabilitiesFor(activeChatType).conversation) {
        fail({ code: 'upload_failed', message: label('attachments_unavailable', 'Attachments are not available for this chat.'), retryable: false });
        return failedEntry();
      }

      // The widget's cap, enforced in its input component rather than the
      // shared validator, so it has to be applied here as well.
      const existing = store.getState().attachments;
      if (existing.length >= FILE_UPLOAD_CONSTANTS.MAX_FILES) {
        fail({
          code: 'upload_failed',
          message: `You can only attach up to ${FILE_UPLOAD_CONSTANTS.MAX_FILES} files.`,
          retryable: false,
        });
        return failedEntry();
      }

      // Same checks as the widget: name, size, type, and not already attached.
      const checked = validateAndPrepareFiles([file], {
        existingFiles: existing.map(a => ({ name: a.name, size: a.size, type: a.type })),
      });
      if (checked.validFiles.length === 0) {
        const rejection = checked.invalidFiles[0]?.error;
        const message = rejection?.message || 'That file cannot be attached.';
        fail({ code: 'upload_failed', message, retryable: false });
        void logFileUploadError(transportConfig(), {
          chatType: activeChatType,
          fileType: file.type,
          fileSize: file.size,
          errorStatusCode: uploadErrorCode(rejection),
        });
        return failedEntry();
      }

      const { meta } = checked.validFiles[0];
      const entry: AttachmentUpload = {
        id: meta.id,
        name: meta.name,
        size: file.size,
        type: file.type,
        status: 'uploading',
        progress: 0,
      };
      store.update(s => ({ attachments: [...s.attachments, entry], error: null }));

      const patchAttachment = (patch: Partial<AttachmentUpload>) =>
        store.update(s => ({ attachments: s.attachments.map(a => (a.id === meta.id ? { ...a, ...patch } : a)) }));
      const stillWanted = () => store.getState().attachments.some(a => a.id === meta.id);

      try {
        const [signed] = await requestUploadUrls(transportConfig(), authInfo().conversationId, [meta]);
        // Removed while the URL was being fetched: nothing to send, nothing to report.
        if (!stillWanted()) return { ...entry, status: 'failed' };

        const upload = uploadToStorage(file, meta, signed, progress => patchAttachment({ progress }));
        activeUploads.set(meta.id, upload.abort);
        let stored;
        try {
          stored = await upload.done;
        } finally {
          activeUploads.delete(meta.id);
        }

        // A conversation that already exists is told about the file now; one
        // attached before the first message is announced by that message.
        const conversationId = authInfo().conversationId;
        if (conversationId) {
          await notifyBackend(transportConfig(), conversationId, [
            { id: meta.id, name: meta.name, fileSize: file.size, downloadUrl: stored.uploadUrl, path: stored.path },
          ]);
        }

        patchAttachment({ status: 'uploaded', progress: 1, url: stored.downloadUrl });
        return store.getState().attachments.find(a => a.id === meta.id) || { ...entry, status: 'uploaded', url: stored.downloadUrl };
      } catch (e) {
        if (e instanceof FileUploadError && e.code === 'UPLOAD_ABORTED') {
          // removeAttachment already took the entry out of state.
          return { ...entry, status: 'failed' };
        }
        logError('attachments: upload failed', e, { name: meta.name });
        patchAttachment({ status: 'failed' });
        fail({
          code: 'upload_failed',
          message: e instanceof FileUploadError ? e.message : 'File upload failed. Please try again.',
          retryable: true,
        });
        void logFileUploadError(transportConfig(), {
          chatType: activeChatType,
          fileType: file.type,
          fileSize: file.size,
          errorStatusCode: uploadErrorCode(e),
        });
        return store.getState().attachments.find(a => a.id === meta.id) || { ...entry, status: 'failed' };
      }
    },

    removeAttachment(id: string) {
      const abort = activeUploads.get(id);
      if (abort) {
        activeUploads.delete(id);
        abort();
      }
      store.update(s => ({ attachments: s.attachments.filter(a => a.id !== id) }));
    },

    async sendFeedback(feedback: 'positive' | 'negative') {
      try {
        await postFeedback({ serverConfig: transportConfig(), feedback });
        store.setState({ error: null, feedbackSubmitted: true });
      } catch (e) {
        // A soft failure, like every other command: the transport refuses
        // before there is a conversation to rate, and the server can say no.
        // Throwing here would surface as an unhandled rejection in a host that
        // wired a button to it.
        logError('the rating could not be sent', e);
        fail({
          code: 'feedback_failed',
          message: label('rating_failed', 'Your rating could not be sent. Please try again.'),
          retryable: true,
        });
      } finally {
        // Asked and answered either way. Leaving the prompt up after a failed
        // send invites them to rate the same chat twice.
        store.setState({ feedbackRequested: false });
      }
    },

    reset() {
      // Every place the core keeps anything, in one list, so "start fresh"
      // means the same thing everywhere and a host never reaches into
      // storage to do it by hand.
      teardownConversation();
      dismissCaptcha();
      removeLiveChatAuthInfo(options.locationId, options.widgetId);
      clearRequestTokenSession(options.locationId, options.widgetId);
      tokenInFlight = null;
      opened = false;
      openInFlight = null;

      const config = store.getState().config;
      const configured = (config?.chatType as ChatType) || options.chatType;
      activeChatType = configured;
      const { detectedCountry, detectedCountryResolved } = store.getState();
      store.setState({
        ...initialState(configured),
        detectedCountry,
        detectedCountryResolved,
        config,
        requestState: config ? 'completed' : 'init',
        awaitingChatTypeSelection: !!config && isSelectable(configured),
      });
      warn('reset: forgot this visitor for the widget; call open() to begin again (not after close(), which is terminal)');
    },

    async loadOlderMessages() {
      const { conversationId } = authInfo();
      if (!historyCursor || !conversationId || store.getState().historyLoading) return;
      const cursor = historyCursor;
      store.setState({ historyLoading: true, error: null });
      try {
        const response = await getPreviousChatMessages({
          serverConfig: transportConfig(),
          conversationId,
          lastMessageId: cursor,
        });
        const older = (response?.messages || []).map(toChatMessage).reverse();
        // The page before this one ended at `cursor`; the server may repeat
        // that message as the first of the next page.
        historyCursor = response?.lastMessageId || null;
        store.update(s => {
          const seen = new Set(s.messages.map(m => m.id));
          const fresh = older.filter(m => !seen.has(m.id));
          return { messages: [...fresh, ...s.messages], hasMoreHistory: historyCursor !== null, historyLoading: false };
        });
      } catch (e) {
        // The cursor is kept so the visitor can try again; the widget leaves
        // its button in place on a 429 for the same reason.
        store.setState({ historyLoading: false });
        if (e instanceof TooManyRequestsError) {
          fail({ code: 'rate_limited', message: label('too_many_requests', 'Too many requests just now. Please wait a moment and try again.'), retryable: true });
          return;
        }
        logError('could not load older messages', e, { conversationId });
        fail({ code: 'send_failed', message: label('older_messages_failed', 'Older messages could not be loaded. Please try again.'), retryable: true });
      }
    },

    async restartConversation() {
      // What the widget's "start a new chat" does (pane.RemoveFeedback): the
      // transcript stays on screen, the visitor stays who they are, the ended
      // flag is cleared, and the composer opens again. The next message
      // greets afresh and either continues the thread or, if the server has
      // closed it, is answered with "clear conversation id and token" — which
      // the transport handles by dropping the identity so the send after that
      // starts a new conversation. Forgetting everything is reset()'s job.
      disconnectConversation();
      conversationStarted = false;
      outsideHoursMessageShown = false;
      manageLiveChatAuthInfo({
        data: {
          locationId: options.locationId,
          widgetId: options.widgetId,
          socketConnected: false,
          chatEndedDueToInactivity: false,
        },
      });

      const outside = outsideHours();
      store.setState({
        agentTyping: false,
        ended: false,
        endedReason: null,
        feedbackRequested: false,
        feedbackSubmitted: false,
        // Outside hours the widget asks for details again instead of
        // reopening the composer; inside hours it just reopens it.
        contactDetailsRequested: outside && !store.getState().contactFormSubmitted,
        error: null,
        connection: 'idle',
        canSend: capabilitiesFor(activeChatType).conversation && !outside,
      });
    },
    startVoiceCall: () => notImplemented('startVoiceCall'),
    endVoiceCall: () => notImplemented('endVoiceCall'),
    setMicMuted: () => undefined,
  };
}

/** Exposed for the client's own tests; not part of the public surface. */
export const __testing = { initialState, capabilitiesFor, isSelectable };
